<script setup lang="ts">
  // 動画再生時のコントローラー表示コンポーネント

  import { useDisplay } from "vuetify";
  import { differenceInMilliseconds } from "date-fns";

  interface Props {
    currentTime?: number;
    maxTime?: number;
    volume?: number;
    fullscreen: boolean;
    paused?: boolean;
    isVideoMuted?: boolean;
    isAudioMuted?: boolean;

    playbackRate?: number;

    showSlider?: boolean;
    showPlayButton?: boolean;
    showFullscreenButton?: boolean;
    muteButtonType?: "all" | "audio"; // 無し:表示なし。button:icon:ミュートボタンを表示する。ミュート状態を表すアイコンを表示する
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "clickPlay"): void;
    (event: "clickFullscreen"): void;
    (event: "clickMuteVideo"): void;
    (event: "clickMuteAudio"): void;
    (event: "clickPip"): void;
    (event: "clickMove", value: number): void;
    (event: "update:currentTime", currentTime: number): void;
    (event: "update:playbackRate", playbackRate: number): void;
    (event: "update:volume", volume: number): void;
  }
  const emit = defineEmits<Emits>();

  // スマホ/タブレットかどうか
  const { platform } = useDisplay();
  const isSpOrTablet = computed<boolean>(() => platform.value.android || platform.value.ios);
  const isIOS = computed<boolean>(() => platform.value.ios);

  // 再生ボタンクリックイベント
  const clickPlay = event => {
    event.stopPropagation();
    emit("clickPlay");
  };
  const clickMove = (event: any, value: number) => {
    event.stopPropagation();
    showControls(5000);
    emit("clickMove", value);
  };
  // ミュートボタンクリックイベント
  const clickMuteVideo = event => {
    event.stopPropagation();
    emit("clickMuteVideo");
  };
  const clickMuteAudio = event => {
    event.stopPropagation();
    emit("clickMuteAudio");
  };
  // フルスクリーンボタンクリックイベント
  const clickFullscreen = event => {
    if (props.showFullscreenButton) {
      event.stopPropagation();
      emit("clickFullscreen");
    }
  };
  // スライダークリック時にはクリックイベントを透過しない
  const clickSlider = event => {
    event.stopPropagation();
  };

  // SP版で左端スワイプで戻る処理をキャンセルする
  // Androidではclickイベントが発生しなくなるため、@touchstartに設定してはいけない。
  const onTouchStart = event => {
    if (event.pageX > 20 && event.pageX < window.innerWidth - 20) return;
    event.preventDefault();
  };

  // 動画時間
  const videoCurrentTime = computed<number | undefined>({
    get: () => props.currentTime,
    set: value => {
      emit("update:currentTime", value);
    },
  });

  const videoPlaybackRate = computed<number>({
    get: () => props.playbackRate,
    set: value => {
      emit("update:playbackRate", value);
    },
  });

  const videoVolume = computed<number>({
    get: () => props.volume ?? 100,
    set: (value: string) => {
      emit("update:volume", Number(value));
    },
  });

  // currentTimeと動画時間を指定のフォーマットにして返す
  const timeToString = (value: number, maxValue: number) => {
    if (value) {
      const seconds = Math.floor(value % 60);
      const minutes = Math.floor((value / 60) % 60);
      const hours = Math.floor((value / 60 / 60) % 60);
      const maxHours = Math.floor((maxValue / 60 / 60) % 60);

      if (hours > 0 || maxHours > 0) {
        return `${zeroPadding(hours, 2)}:${zeroPadding(minutes, 2)}:${zeroPadding(seconds, 2)}`;
      }
      return `${zeroPadding(minutes, 2)}:${zeroPadding(seconds, 2)}`;
    }
    return "--:--";
  };

  const HideControlsMilliSecond = 1500; // 無操作状態でコントロールを非表示にする時間

  const isShowControls = ref<boolean>(false); // コントロールを表示するかどうか
  const isMenuOpen = ref<boolean>(false); // メニューが開いているかどうか
  let switchControlsDate: Date = new Date(); // コントロール表示状態を切り替えたDateTime

  // コントロールを非表示にする
  const hideControls = () => {
    if (isMenuOpen.value) return;
    // モバイル版safariの不具合対策。スライダーを触れてそのまま非表示にした後に、タップして表示するとすぐ消えることがあるのでその対策として表示してから300msは非表示にしないようにしている。
    const currentDate = new Date();
    const milliseconds = differenceInMilliseconds(currentDate, switchControlsDate);
    if (milliseconds >= 300) {
      isShowControls.value = false;
      clearHideControlsTimer();
      switchControlsDate = new Date();
    }
  };
  // コントロールを表示する
  const showControls = (hideMilliSecont = HideControlsMilliSecond) => {
    if (isShowControls.value) {
      setHideControlsTimer(hideMilliSecont);
      return;
    }
    // PC版safariの不具合対策。コントロールを消した後に何故か一度mouseMoveイベントが起きる。その対策として消してから700msは表示しないようにしている。
    const currentDate = new Date();
    const milliseconds = differenceInMilliseconds(currentDate, switchControlsDate);
    if (milliseconds >= 700) {
      isShowControls.value = true;
      setHideControlsTimer(hideMilliSecont);
      switchControlsDate = new Date();
    }
  };

  // スクリーンクリックイベント
  const onClickScreen = event => {
    if (isSpOrTablet.value) {
      // SP or Tabletでは表示、非表示を切り替える
      if (isShowControls.value) {
        // 表示中であればクリア
        hideControls();
      } else {
        // 非表示中であれば表示
        showControls();
      }
    } else {
      // PC版では画面クリックで再生を行う
      clickPlay(event);
    }
  };

  // clickとダブルクリックを排他制御するためのtimeout用
  let clickTimeoutId: any = null;

  // マウスイベント
  const onClick = event => {
    // クリックイベントを処理
    if (clickTimeoutId === null) {
      // タイムアウトが設定されていない場合はクリックとみなす
      clickTimeoutId = setTimeout(() => {
        // 一定時間後に実行される
        clickTimeoutId = null;
        onClickScreen(event);
      }, 300); // 300ミリ秒はダブルクリックの許容時間
    }
  };

  // ダブルクリックイベントを処理
  const onDoubleClick = () => {
    // クリックタイムアウトをクリア
    clearTimeout(clickTimeoutId);
    clickTimeoutId = null;

    if (props.showFullscreenButton) {
      // ダブルクリックで全画面終了処理
      emit("clickFullscreen");
    }
  };

  const onMouseMove = () => {
    // 領域内でカーソルが動いたら表示させる
    showControls();
  };
  const onMouseLeave = () => {
    // カーソルが領域から外れたら非表示にする
    hideControls();
  };

  // 自動非表示のためのタイマー設定
  let hideControlsTimerId: any;
  const setHideControlsTimer = (hideMilliSecont = HideControlsMilliSecond) => {
    clearHideControlsTimer();
    hideControlsTimerId = setTimeout(() => {
      hideControls();
    }, hideMilliSecont);
  };

  // 削除
  const clearHideControlsTimer = () => {
    if (hideControlsTimerId) {
      clearTimeout(hideControlsTimerId);
      hideControlsTimerId = undefined;
    }
  };

  // PictureInPicture 対応チェック
  const showPipButton = document.pictureInPictureEnabled;

  watch(isMenuOpen, val => {
    if (val) {
      clearHideControlsTimer();
    } else {
      setHideControlsTimer();
    }
  });
</script>

<template>
  <v-sheet
    class="custom-player-controls w-100 h-100"
    data-testid="custom-player-controls"
    color="transparent"
    @click="onClick"
    @mousemove="isSpOrTablet ? null : onMouseMove()"
    @mouseleave="isSpOrTablet ? null : onMouseLeave()"
    @touchmove="isSpOrTablet ? onMouseMove() : null"
    @dblclick="onDoubleClick"
    @touchstart="
      (event: any) => {
        isIOS ? onTouchStart(event) : null;
      }
    "
  >
    <Transition name="fade">
      <v-sheet v-show="isShowControls" class="w-100 h-100" color="transparent" style="position: relative">
        <button v-if="showPlayButton" class="play-button" @click="clickPlay" @dblclick.stop>
          <v-icon v-if="paused" icon="$mdiPlayCircle" color="rgba(17, 156, 246, 1)" size="72px"></v-icon>
          <v-icon v-else icon="$mdiPauseCircle" size="72px" color="rgba(17, 156, 246, 1)"></v-icon>
        </button>
        <v-slider
          v-if="showSlider"
          v-model="videoCurrentTime"
          class="seek-bar px-4"
          color="main-01"
          track-color="monochrome-06"
          :min="0"
          :max="maxTime"
          :step="1"
          :thumb-size="18"
          :track-size="2"
          @click="clickSlider"
        ></v-slider>
        <v-sheet v-if="showSlider" class="current-time rounded-xl text-center">
          {{ timeToString(videoCurrentTime, maxTime) }} / {{ timeToString(maxTime) }}
        </v-sheet>
        <v-sheet class="actions">
          <MoleculesPictureInPictureButton
            v-if="showSlider && showPipButton"
            class="action-btn"
            :class="{ 'hide-pip-btn': isIOS }"
            :width="28"
            @click="() => emit('clickPip')"
          />

          <MoleculesPlaySpeedButton
            v-if="showSlider"
            v-model:playback-rate="videoPlaybackRate"
            class="action-btn"
            :width="24"
            @on-menu-switch="val => (isMenuOpen = val)"
          />
          <template v-if="muteButtonType === 'audio'">
            <MoleculesMuteAudioButton :is-muted="isAudioMuted" class="action-btn" :width="28" @click="clickMuteAudio" />
          </template>
          <template v-else-if="muteButtonType === 'all'">
            <MoleculesMuteVideoButton :is-muted="isVideoMuted" class="action-btn" :width="28" @click="clickMuteVideo" />
            <MoleculesMuteAudioButton :is-muted="isAudioMuted" class="action-btn" :width="28" @click="clickMuteAudio" />
          </template>
          <template v-if="['all', 'audio'].includes(muteButtonType || '') && !isSpOrTablet">
            <input
              v-model="videoVolume"
              :disabled="isVideoMuted || isAudioMuted ? true : false"
              type="range"
              min="1"
              max="100"
              class="slider"
              @click="clickSlider"
            />
          </template>
          <template v-if="showFullscreenButton">
            <MoleculesFullScreenButton :is-on="fullscreen" class="action-btn" :width="28" @click="clickFullscreen" />
          </template>
        </v-sheet>

        <button v-if="showPlayButton" class="speed-skip-btn forward" @click="e => clickMove(e, 10)" @dblclick.stop>
          <v-icon icon="$mdiFastForward10" size="32px" color="white"></v-icon>
        </button>
        <button v-if="showPlayButton" class="speed-skip-btn backward" @click="e => clickMove(e, -10)" @dblclick.stop>
          <v-icon icon="$mdiRewind10" size="32px" color="white"></v-icon>
        </button>
      </v-sheet>
    </Transition>
    <template v-if="showPlayButton">
      <div class="play-icon-area">
        <div v-if="paused" class="play-icon rounded-circle d-flex align-center justify-center">
          <v-icon icon="$mdiPause" size="32px" class="mdi-pause"></v-icon>
        </div>
        <div v-else class="play-icon rounded-circle d-flex align-center justify-center">
          <v-icon icon="$mdiPlay" size="32px" class="mdi-play"></v-icon>
        </div>
      </div>
    </template>
  </v-sheet>
</template>

<style lang="scss" scoped>
  .custom-player-controls {
    z-index: 2;
    position: absolute;
    top: 0;
  }

  .play-button {
    height: 70px;
    width: 70px;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    padding: 0;
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0);
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
  }

  .seek-bar {
    position: absolute;
    bottom: 10px;
    left: -8px;
    width: 100%;
  }

  .current-time {
    position: absolute;
    bottom: 12px;
    left: 15px;
    padding: 2px 14px 2px 14px;

    font-size: 12px !important;
    font-weight: 700;
    line-height: 16px; // 個別対応。時間表示の文字間を詰める
    letter-spacing: 0em !important; // 個別対応。時間表示の文字間を詰める
    text-transform: none !important;

    background: rgba(var(--v-theme-main-02), 0.5);
  }

  .actions {
    display: flex;
    position: absolute;
    bottom: 10px;
    right: 15px;
    gap: 18px;
    background: none;
  }
  .action-btn {
    height: 28px;
    width: 28px;
    padding: 0;
    background-color: rgba(0, 0, 0, 0);
  }
  .hide-pip-btn {
    @media (display-mode: standalone) {
      display: none;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .play-icon-area {
    height: 72px;
    width: 72px;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    padding: 0;
    pointer-events: none;
    background-color: rgba(0, 0, 0, 0);
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
  }
  .play-icon {
    height: 72px;
    width: 72px;

    opacity: 0;
    animation-name: big;
    animation-duration: 0.7s;
    animation-timing-function: ease;
    background: rgba(var(--v-theme-main-02), 0.5);
  }

  .speed-skip-btn {
    height: 48px;
    width: 48px;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    padding: 0;
    cursor: pointer;
    background-color: rgba(17, 156, 246, 1);

    &.forward {
      margin-left: 100px;
    }
    &.backward {
      margin-left: -97px;
    }

    &:active {
      transform: translate(-50%, -50%) scale(1.1);
      background-color: rgba(17, 156, 246, 1);
    }

    &.animate {
      animation: big 0.7s ease;
    }
  }

  @keyframes big {
    0% {
      opacity: 1;
      transform: scale(1);
      transform-origin: 50% 50%;
    }
    100% {
      opacity: 0;
      transform: scale(1.5);
      transform-origin: 50% 50%;
    }
  }

  .slider {
    width: 80px;
    height: 10px;
    border-radius: 5px;
    background: #d3d3d3;
    outline: none;
    margin-left: -10px;
    margin-top: 10px;
    appearance: none;
    background-color: rgba(54, 30, 255, 0.3) !important;
  }
  .slider::-webkit-slider-thumb {
    appearance: none;
    background-color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
  }

  .slider::-moz-range-thumb {
    background-color: crimson;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    border: none;
  }
</style>
